import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion).
// askBrain now sends the message + this app's declared action catalog to the generic fleet
// chat brain (/api/apps/oriora_lockbox/chat) — ONE billed call returns ONE registered action
// + a reply; the app resolves/executes it locally so whole documents stay on-device. The
// regex intent-router below is the FREE FALLBACK (offline / signed-out / out of funds) — chat
// never goes dead. Original prototype notes:
//   • "add document Lease: <text>"       → local store.addDocument            → add_document
//   • "delete <title>"                   → resolve id locally                 → delete_document (UI confirms)
//   • "upcoming / deadlines / what's due" → local key-date ledger             → deadlines
//   • "search <term> / find <term>"      → local FTS5 (store.searchPassages)  → search   (free)
//   • a real question ("what does my lease say…") → EXISTING /ask endpoint    → ask      (billed, fenced)
//   • anything else                      → none + a short clarifying nudge
//
// ⚠️ SENSITIVE DOMAIN — DOCUMENT CLERK, NOT AN ADVISER (HARD, MANDATORY pre-LLM on BOTH
// paths). Lockbox quotes and locates what the user's OWN documents say; it NEVER interprets
// their legal, insurance, financial, tax or medical position, predicts outcomes, or advises.
// isAdviceQuestion() runs on the raw message BEFORE serverChat is ever called — an advice-
// seeking ask ("should I sue", "am I covered") is refused for FREE, so it never reaches the
// (billed) routing call either, not just the (billed) /ask call one step later (which keeps
// its own fence too — defence in depth, mirrors brain.ts::askDocuments). This mirrors the
// pre-graduation design, which ran the fence first on the ASK branch before any billed call —
// now it runs first on EVERY message, fleet-standard.
//
// The "ask" action itself stays a TWO-STEP local resolve (unlike lighter actions elsewhere in
// the fleet): the ChatAction union's `ask` variant already carries the FULLY-RESOLVED answer +
// citations (not just a question string), so toAction() below performs the on-device passage
// search AND calls the existing billed /ask endpoint itself — exactly what the original
// keyword router did. That's a deliberate deviation from the lighter "make"/"explain" shape
// used elsewhere (where the second billed call runs later, inside chat/actions.ts) — forced by
// actions.ts's ChatAction type, which this migration must not change.

import { store, todayLocal, type DocKind, type DocumentMeta } from "../store";
import { askDocuments, isAdviceQuestion, AdviceBlockedError, BrainError, brainCall } from "../brain";
import type { ChatAction, ResultRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: ResultRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // ADVICE FENCE: kept "" — never surface the model's freeform reply.
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of isAdviceQuestion + reply-discard).
const SCOPE =
  "Hard limits: never give legal, insurance, financial, tax, or medical advice, never interpret the user's rights/" +
  "coverage/liability/obligations, never say whether to sue/sign/pay/claim or predict an outcome — decline and point " +
  "to a qualified professional. This app is a document VAULT: store, search, and DESCRIBE what the user's OWN saved " +
  "documents literally say, track deadlines; back up to their own cloud.";

const HELP =
  "I can save documents, search them, list upcoming deadlines, and answer questions about what they say — just tell me. Try:\n" +
  '• "add document Lease: <paste the text>"\n' +
  '• "search deposit"\n' +
  '• "what\'s due soon?"\n' +
  '• "what does my lease say about notice?"\n' +
  '• "delete my old policy"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Question-shaped → route to the grounded (billed) /ask endpoint (keyword-fallback only).
const QUESTION_RE = /(\?|\b(what|which|when|where|how|does|do|is|are|can|could|who|why|whose|tell me)\b)/i;
// Explicit "list my deadlines" intents — kept tight so genuine questions ("when does X expire") still go to ask.
const DEADLINE_RE = /\b(upcoming|deadlines?|due soon|coming up|key dates?|what'?s due|whats due|expiring soon|renewals due)\b/i;

function guessKind(text: string): DocKind {
  const t = text.toLowerCase();
  if (/\b(lease|tenanc|rent|landlord|tenant)\b/.test(t)) return "lease";
  if (/\b(insurance|policy|premium|cover(age)?|insured|claim)\b/.test(t)) return "insurance";
  if (/\b(warrant|guarantee|receipt|purchase)\b/.test(t)) return "warranty";
  if (/\b(passport|licence|license|identity|national id|id card)\b/.test(t)) return "id";
  if (/\b(utility|electric|gas|water|broadband|council tax|bill)\b/.test(t)) return "utility";
  return "other";
}

function pickList(docs: { title: string }[]): string {
  return docs.slice(0, 6).map((d) => `• ${d.title}`).join("\n");
}

/** Documents adapted for the FLEET fuzzy resolver (see ./fuzzy), which keys on `name`.
 *  Forgiving lookup — tolerant of misspellings, dropped words and word order — so a paraphrased
 *  title still resolves the right document for delete / attach-photo. Resolution stays client-side. */
type NamedDoc = DocumentMeta & { name: string };
async function docsNamed(): Promise<NamedDoc[]> {
  const docs = await store.listDocuments();
  return docs.map((d) => ({ ...d, name: d.title }));
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }
  // DOCUMENT-CLERK FENCE — pre-LLM, free, on BOTH paths. An advice-seeking ask is refused
  // deterministically before it can reach EITHER billed call (routing OR ask).
  if (isAdviceQuestion(text)) { const r = none(new AdviceBlockedError().message); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (title text → id) and runs the ONE grounded billed
// /ask call locally, because the documents live here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.lockbox.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_document", description: "Save a new document to the vault.", params: { title: "the document's title", body: "the document's full text, as the user pasted it" } },
  { name: "delete_document", description: "Delete one saved document identified by its title (the app asks the user to confirm first).", params: { title: "which document — title text as the user wrote it" } },
  { name: "search", description: "Free-text search over the saved documents (no interpretation, just matching passages).", params: { query: "the search text" } },
  { name: "deadlines", description: "List upcoming key dates (renewals, expiries, notices, dues) derived from the user's documents." },
  { name: "ask", description: "Answer a real question using ONLY the user's saved documents, with citations — quotes and locates what a document states. NEVER for legal, insurance, financial, tax or medical advice; never interprets the user's position or predicts an outcome.", params: { question: "the user's question, as asked" } },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (document scans), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE document collection instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const docs = await store.listDocuments();
  const list = docs.slice(0, 40).map((d) => `${d.title} (${d.kind})`).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Documents: ${list || "(none yet)"}.`) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // ADVICE FENCE: never surface the model's freeform reply — reply stays "".
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: "", actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export async function toAction(r: ServerChat, rawText: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_document": {
      const title = asStr(p.title);
      const body = asStr(p.body);
      if (!title || !body) return none(say || 'Give me a title and the text, e.g. "add document Lease: <paste the document text>".');
      return { reply: say, action: { kind: "add_document", title, docKind: guessKind(`${title} ${body}`), body } };
    }
    case "delete_document": {
      const term = asStr(p.title);
      if (!term) return none(say || "Which document should I remove?");
      const named = await docsNamed();
      const matches = findNamedMatches(named, term);
      if (matches.length === 0) return none(notFoundMessage(named, term));
      if (matches.length > 1) return none(`I found ${matches.length} matching "${term}" — be more specific:\n${pickList(matches)}`);
      return { reply: say, action: { kind: "delete_document", id: matches[0].id, label: `"${matches[0].title}"` } };
    }
    case "search": {
      const query = asStr(p.query) ?? rawText;
      return { reply: "", action: { kind: "search", query } };
    }
    case "deadlines":
      return { reply: say, action: { kind: "deadlines" } };
    case "ask": {
      // The DOCUMENT-CLERK FENCE — re-checked here as defence in depth (askDocuments checks
      // it again too). The user's message already passed the pre-serverChat fence in askBrain,
      // but the model-resolved "question" param is re-checked in case it differs from the raw text.
      const question = asStr(p.question) ?? rawText;
      if (isAdviceQuestion(question)) return none(new AdviceBlockedError().message);
      const passages = await store.searchPassages(question, 12);
      if (passages.length === 0) {
        return none("I couldn't find that in your documents. Try different words, or add the document that would have it.");
      }
      const docIds = [...new Set(passages.map((pg) => pg.doc_id))];
      try {
        const facts = await store.headlineFactsForDocs(docIds, todayLocal(), 6);
        const ar = await askDocuments(question, passages, facts);
        return { reply: "", action: { kind: "ask", answer: ar.answer, citations: ar.citations, confidence: ar.confidence } };
      } catch (e) {
        if (e instanceof AdviceBlockedError || e instanceof BrainError) return none(e.message);
        throw e;
      }
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);

  // 1) ADD DOCUMENT — "add document <title>: <body>". Needs both title and body text.
  const addM = t.match(/^(?:add|save|store)\s+(?:a\s+|the\s+|new\s+)?(?:document|doc|file|paper)\b[\s:.\-]*([\s\S]+)$/i);
  if (addM) {
    const rest = addM[1].trim();
    const sep = rest.match(/^["']?(.+?)["']?\s*[:\n\-–—]\s*([\s\S]+)$/);
    if (!sep) {
      return none('Give me a title and the text, e.g. "add document Lease: <paste the document text>".');
    }
    const title = sep[1].trim();
    const body = sep[2].trim();
    if (!title || !body) return none("I need both a title and the document text.");
    return { reply: "", action: { kind: "add_document", title, docKind: guessKind(`${title} ${body}`), body } };
  }

  // 2) DELETE — resolve the target document locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|forget)\s+(?:the\s+)?(?:document\s+|doc\s+|file\s+)?(?:titled\s+|called\s+|named\s+|my\s+|about\s+)?["']?(.+?)["']?\s*$/i);
  if (delM) {
    const term = delM[1].trim().replace(/[.?!]+$/, "");
    if (!term) return none("Which document should I remove?");
    const named = await docsNamed();
    const matches = findNamedMatches(named, term);
    if (matches.length === 0) return none(notFoundMessage(named, term));
    if (matches.length > 1) return none(`I found ${matches.length} matching "${term}" — be more specific:\n${pickList(matches)}`);
    return { reply: "", action: { kind: "delete_document", id: matches[0].id, label: `"${matches[0].title}"` } };
  }

  // 3) DEADLINES — the on-device key-date ledger (free).
  if (DEADLINE_RE.test(t)) return { reply: "", action: { kind: "deadlines" } };

  // 4) SEARCH — explicit search verb → local FTS5 (free, no bill).
  const searchM = t.match(/^(?:search|find|look\s+up|look\s+for|show\s+me)\s+(?:for\s+)?(.+)$/i);
  if (searchM && !QUESTION_RE.test(searchM[1])) {
    const query = searchM[1].trim().replace(/[.?!]+$/, "");
    if (query) return { reply: "", action: { kind: "search", query } };
  }

  // 5) ASK — a real question → the ONE billed, grounded /ask call. Runs the advice fence first.
  if (QUESTION_RE.test(t)) {
    if (isAdviceQuestion(t)) return none(new AdviceBlockedError().message);
    const passages = await store.searchPassages(t, 12);
    if (passages.length === 0) {
      return none("I couldn't find that in your documents. Try different words, or add the document that would have it.");
    }
    const docIds = [...new Set(passages.map((p) => p.doc_id))];
    try {
      const facts = await store.headlineFactsForDocs(docIds, todayLocal(), 6);
      const r = await askDocuments(t, passages, facts);
      return { reply: "", action: { kind: "ask", answer: r.answer, citations: r.citations, confidence: r.confidence } };
    } catch (e) {
      if (e instanceof AdviceBlockedError || e instanceof BrainError) {
        return none(e.message);
      }
      throw e;
    }
  }

  // 6) Unclear → a short clarifying nudge.
  return none(HELP);
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
